import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import {
  Typography, Select, DatePicker, Table, Space, Spin, Empty,
  Card, Statistic, Row, Col, Tag, Segmented, Tooltip, Switch,
} from 'antd';
import {
  TeamOutlined, CheckCircleOutlined, ClockCircleOutlined, BarChartOutlined,
  UserOutlined, CalendarOutlined, ScheduleOutlined, DollarOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import AgentLink from '../components/AgentLink';
import { useNavigate } from 'react-router-dom';

// ── helpers ────────────────────────────────────────────────────────────────
function monthLabel(mk) {
  if (!mk) return '';
  // Accept both YYYYMM and YYYY-MM
  const s = String(mk).replace('-', '');
  return dayjs(s, 'YYYYMM').format('MMM YYYY');
}

function adherenceColor(pct) {
  if (pct === null || pct === undefined) return '#8c8c8c';
  if (pct > 97) return '#52c41a';
  if (pct >= 90) return '#faad14';
  return '#ff4d4f';
}

function AdherenceTag({ pct }) {
  if (pct === null || pct === undefined) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  return <span style={{ color: adherenceColor(pct), fontWeight: 600 }}>{pct.toFixed(1)}%</span>;
}

// Problem % color: lower is better (inverse of adherence coloring)
function problemColor(pct) {
  if (pct === null || pct === undefined) return '#8c8c8c';
  if (pct <= 10) return '#52c41a';
  if (pct <= 25) return '#faad14';
  return '#ff4d4f';
}

function statusLabel(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct > 97)  return { text: 'Good',           color: 'success' };
  if (pct >= 90) return { text: 'Watch',           color: 'warning' };
  return               { text: 'Needs Attention',  color: 'error'   };
}

const DOW_ORDER      = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHIFT_ORDER    = ['Morning', 'Afternoon', 'Night'];
const TENURE_BUCKETS = ['< 3 Months', '3M - 1 Year', '1 - 2 Years', '2 - 3 Years', '3+ Years'];
const SHIFT_ICONS  = { Morning: '☀️', Afternoon: '🌤️', Night: '🌙' };

// ── component ──────────────────────────────────────────────────────────────
// The four disjoint slices of non-worked paid time. PTO and Other are net of
// the parts now broken out beside them, so the four never double-count.
// All four are context for a shortfall against the schedule, never inputs to
// adherence or lost revenue.
const hrsCell = v => (v != null && v > 0)
  ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  : '—';

const TIME_OFF_COLS = [
  ['PTO',     'PTOOnlyHrs',   110, 'Paid time off, excluding sick. Not part of adherence or lost revenue.'],
  ['Sick',    'SickHrs',      100, 'Sick hours. Not part of adherence or lost revenue.'],
  ['Holiday', 'HolidayHrs',   110, 'Unworked holiday, earning code HOL. Holiday actually worked is HOL1.5 and counts in Hours Worked instead.'],
  ['Other',   'OtherOnlyHrs', 110, 'Any other earning code, excluding holiday. Not part of adherence or lost revenue.'],
].map(([label, key, width, tip]) => ({
  title: <Tooltip title={tip}>{label} <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>,
  dataIndex: key, key, width, align: 'right',
  sorter: (a, b) => (a[key] || 0) - (b[key] || 0),
  render: hrsCell,
}));

/**
 * `embedded` renders without its heading or team/month pickers, scoped by the
 * team and window passed in. Used by the Campaign Summary's Adherence tab.
 *
 * Team, not campaign: adherence is recorded against a team and nothing in the
 * data joins a team to a campaign.
 */
export default function AdherencePage({ embedded = false, teamId = null, from = null, to = null }) {
  const navigate          = useNavigate();
  const currentUser       = useContext(UserContext);
  const isAdmin           = currentUser?.Role === 'admin';
  const isQaAdmin         = currentUser?.Role === 'qa_admin';
  const canViewFinancials = currentUser?.CanViewFinancials ?? 0;

  const [allTeams,     setAllTeams]     = useState([]);
  const [allJobs,      setAllJobs]      = useState([]);
  const [teamIds,      setTeamIds]      = useState([]);
  const [jobIds,       setJobIds]       = useState([]);
  const [fromMonth,    setFromMonth]    = useState(() => dayjs().subtract(2, 'month').startOf('month'));
  const [toMonth,      setToMonth]      = useState(() => dayjs().startOf('month'));
  const [summary,      setSummary]      = useState(null);
  const [rows,         setRows]         = useState([]);
  const [agentRows,    setAgentRows]    = useState([]);
  const [dowRows,      setDowRows]      = useState([]);
  const [shiftRows,    setShiftRows]    = useState([]);
  const [tenureRows,   setTenureRows]   = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [dowLoading,   setDowLoading]   = useState(false);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [viewMode,     setViewMode]     = useState('By Team');
  const [activeOnly,   setActiveOnly]   = useState(false);
  const filtersReady = useRef(false);

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then(res => setAllTeams(res.data)).catch(() => {});
    api.get('/api/jobs').then(res => setAllJobs((res.data || []).filter(j => j.IsActive && j.IncludeInStats))).catch(() => {});
  }, []);

  const teamOptions = (() => {
    // qa_admin follows Visible Teams like everyone else; only admin sees all.
    if (isAdmin) return allTeams;
    const vis = currentUser?.VisibleTeams || [];
    return vis.length ? vis : allTeams;
  })();

  // Embedded, the team and window come from the page above.
  useEffect(() => {
    if (!embedded) return;
    setTeamIds(teamId ? [teamId] : []);
    if (from) setFromMonth(dayjs(from).startOf('month'));
    if (to)   setToMonth(dayjs(to).startOf('month'));
  }, [embedded, teamId, from, to]);

  // Readiness is a fact about the state, not about the effect above having
  // run. Setting it there would be synchronous while setTeamIds is still
  // queued, so the fetch in the same commit would read teamIds as [] --
  // which buildParams turns into no team filter at all, a query for every
  // team. That query is the heavier of the two and its response landed last,
  // overwriting the correct one.
  useEffect(() => {
    if (!embedded) return;
    const matches = teamId
      ? (teamIds.length === 1 && teamIds[0] === teamId)
      : teamIds.length === 0;
    if (matches) filtersReady.current = true;
  }, [embedded, teamId, teamIds]);

  // Default to the current user's own team; fall back to all visible if unassigned.
  // Sets filtersReady so the first fetch waits for this to run.
  useEffect(() => {
    if (embedded) return;
    if (filtersReady.current || !teamOptions.length || !currentUser) return;
    const myTeamId = currentUser.TeamID;
    if (myTeamId && teamOptions.some(t => t.ID === myTeamId)) {
      setTeamIds([myTeamId]);
    } else {
      setTeamIds(teamOptions.map(t => t.ID));
    }
    filtersReady.current = true;
  }, [teamOptions, currentUser]);

  const buildParams = useCallback(() => {
    const p = {
      from_month: fromMonth.format('YYYYMM'),
      to_month:   toMonth.format('YYYYMM'),
    };
    if (teamIds.length) p.team_ids   = teamIds.join(',');
    if (jobIds.length)  p.job_id     = jobIds.join(',');
    if (activeOnly)     p.active_only = 1;
    return p;
  }, [teamIds, jobIds, fromMonth, toMonth, activeOnly]);

  // Requests are not cancelled, so two can be in flight after a quick filter
  // change and the slower one would win on arrival. Only the newest is
  // allowed to write.
  const reqSeq = useRef(0);

  const fetchData = useCallback(async () => {
    if (!fromMonth || !toMonth || !filtersReady.current) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await api.get('/api/adherence', { params: buildParams() });
      if (seq !== reqSeq.current) return;
      setSummary(res.data.summary);
      setRows(res.data.rows || []);
      setAgentRows(res.data.agent_rows || []);
      setTenureRows(res.data.tenure_rows || []);
    } catch {
      if (seq !== reqSeq.current) return;
      setSummary(null); setRows([]); setAgentRows([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [buildParams, fromMonth, toMonth]);

  const fetchDow = useCallback(async () => {
    if (!fromMonth || !toMonth || !filtersReady.current) return;
    setDowLoading(true);
    try {
      const res = await api.get('/api/adherence/dow', { params: buildParams() });
      setDowRows(res.data.rows || []);
    } catch { setDowRows([]); }
    finally { setDowLoading(false); }
  }, [buildParams, fromMonth, toMonth]);

  const fetchShifts = useCallback(async () => {
    if (!fromMonth || !toMonth || !filtersReady.current) return;
    setShiftLoading(true);
    try {
      const res = await api.get('/api/adherence/shifts', { params: buildParams() });
      setShiftRows(res.data.rows || []);
    } catch { setShiftRows([]); }
    finally { setShiftLoading(false); }
  }, [buildParams, fromMonth, toMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (viewMode === 'By Day of Week') fetchDow();   }, [viewMode, fetchDow]);
  useEffect(() => { if (viewMode === 'By Shift')       fetchShifts(); }, [viewMode, fetchShifts]);

  // ── DOW pivot ──────────────────────────────────────────────────────────────
  const dowPivot = useMemo(() => {
    const teams = {};
    for (const r of dowRows) {
      if (!teams[r.TeamID]) teams[r.TeamID] = { TeamID: r.TeamID, TeamName: r.TeamName };
      teams[r.TeamID][r.DayName] = {
        WorkedHrs: r.WorkedHrs, ScheduledHrs: r.ScheduledHrs, AdherencePct: r.AdherencePct,
      };
    }
    return Object.values(teams).sort((a, b) => a.TeamName.localeCompare(b.TeamName));
  }, [dowRows]);

  const tenurePivot = useMemo(() => {
    const teams = {};
    for (const r of tenureRows) {
      if (!teams[r.TeamID]) teams[r.TeamID] = { TeamID: r.TeamID, TeamName: r.TeamName };
      teams[r.TeamID][r.TenureBucket] = {
        WorkedHrs: r.WorkedHrs, ScheduledHrs: r.ScheduledHrs, AdherencePct: r.AdherencePct,
      };
    }
    return Object.values(teams).sort((a, b) => a.TeamName.localeCompare(b.TeamName));
  }, [tenureRows]);

  // ── columns ────────────────────────────────────────────────────────────────
  const teamColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150,
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Month', dataIndex: 'MonthKey', key: 'MonthKey', width: 110,
      sorter: (a, b) => String(a.MonthKey).localeCompare(String(b.MonthKey)),
      defaultSortOrder: 'ascend',
      render: v => monthLabel(v),
    },
    {
      title: 'Hours Worked', dataIndex: 'WorkedHrs', key: 'WorkedHrs', width: 130, align: 'right',
      sorter: (a, b) => (a.WorkedHrs || 0) - (b.WorkedHrs || 0),
      render: v => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Hours Scheduled', dataIndex: 'ScheduledHrs', key: 'ScheduledHrs', width: 120, align: 'right',
      sorter: (a, b) => (a.ScheduledHrs || 0) - (b.ScheduledHrs || 0),
      render: v => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Training Sched', dataIndex: 'TrainingScheduledHrs', key: 'TrainingScheduledHrs', width: 130, align: 'right',
      sorter: (a, b) => (a.TrainingScheduledHrs || 0) - (b.TrainingScheduledHrs || 0),
      render: v => (v != null && v > 0) ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Training Worked', dataIndex: 'TrainingWorkedHrs', key: 'TrainingWorkedHrs', width: 135, align: 'right',
      sorter: (a, b) => (a.TrainingWorkedHrs || 0) - (b.TrainingWorkedHrs || 0),
      render: v => (v != null && v > 0) ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    ...TIME_OFF_COLS,
    {
      title: <Tooltip title="Hours worked divided by hours scheduled, including training time on both sides.">Adherence % <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>,
      key: 'AdhRaw', width: 130, align: 'right',
      sorter: (a, b) => {
        const ra = a.ScheduledHrs > 0 ? a.WorkedHrs / a.ScheduledHrs : -1;
        const rb = b.ScheduledHrs > 0 ? b.WorkedHrs / b.ScheduledHrs : -1;
        return ra - rb;
      },
      render: (_, r) => {
        const pct = r.WorkedHrs != null && r.ScheduledHrs > 0
          ? Math.round(r.WorkedHrs / r.ScheduledHrs * 10000) / 100 : null;
        return <AdherenceTag pct={pct} />;
      },
    },
    {
      title: <Tooltip title="Hours worked divided by hours scheduled with training shifts excluded from both sides. Used as the official adherence metric.">Adherence % Excl Training <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>,
      dataIndex: 'AdherencePct', key: 'AdherencePct', width: 175, align: 'right',
      sorter: (a, b) => (a.AdherencePct ?? -1) - (b.AdherencePct ?? -1),
      render: v => <AdherenceTag pct={v} />,
    },
    ...(canViewFinancials >= 1 ? [{
      title: 'Lost Revenue', dataIndex: 'LostRevenue', key: 'LostRevenue', width: 140, align: 'right',
      sorter: (a, b) => (a.LostRevenue || 0) - (b.LostRevenue || 0),
      render: v => (v != null && v > 0)
        ? <span style={{ color: '#ff4d4f' }}>
            ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        : '—',
    }] : []),
  ];

  const agentColumns = [
    {
      title: 'Adherence Status', dataIndex: 'AdherencePct', key: 'Status', width: 160,
      sorter: (a, b) => (a.AdherencePct ?? -1) - (b.AdherencePct ?? -1),
      defaultSortOrder: 'ascend',
      render: (pct, record) => {
        if (record.EmploymentStatus && record.EmploymentStatus !== 'Active') {
          return <Tag color="default">{record.EmploymentStatus}</Tag>;
        }
        const s = statusLabel(pct);
        if (!s) return <Typography.Text type="secondary">—</Typography.Text>;
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      // Wide enough for a full name on one line. Team gives up the room:
      // its values are short (CNE, ConHome, NextEra) and were never near 180.
      title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200,
      sorter: (a, b) => a.DisplayName.localeCompare(b.DisplayName),
      render: (v, r) => (
        <AgentLink
          name={v}
          url={`/agents?user_id=${r.AgentID}&tab=timecard`}
          navigate={navigate}
          canNavigate={(currentUser?.CanViewAgentSummary ?? 0) >= 1 && !!r.AgentID}
        />
      ),
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 145,
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
    },
    {
      title: 'Emp. Status', dataIndex: 'EmploymentStatus', key: 'EmploymentStatus', width: 120,
      sorter: (a, b) => (a.EmploymentStatus || '').localeCompare(b.EmploymentStatus || ''),
      render: v => {
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        const color = v === 'Active' ? 'success' : v === 'Terminated' ? 'error' : 'default';
        return <Tag color={color}>{v}</Tag>;
      },
    },
    {
      title: 'Hours Worked', dataIndex: 'WorkedHrs', key: 'WorkedHrs', width: 130, align: 'right',
      sorter: (a, b) => (a.WorkedHrs || 0) - (b.WorkedHrs || 0),
      render: v => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Hours Scheduled', dataIndex: 'ScheduledHrs', key: 'ScheduledHrs', width: 120, align: 'right',
      sorter: (a, b) => (a.ScheduledHrs || 0) - (b.ScheduledHrs || 0),
      render: v => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Training Sched', dataIndex: 'TrainingScheduledHrs', key: 'TrainingScheduledHrs', width: 130, align: 'right',
      sorter: (a, b) => (a.TrainingScheduledHrs || 0) - (b.TrainingScheduledHrs || 0),
      render: v => (v != null && v > 0) ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    {
      title: 'Training Worked', dataIndex: 'TrainingWorkedHrs', key: 'TrainingWorkedHrs', width: 135, align: 'right',
      sorter: (a, b) => (a.TrainingWorkedHrs || 0) - (b.TrainingWorkedHrs || 0),
      render: v => (v != null && v > 0) ? v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—',
    },
    ...TIME_OFF_COLS,
    {
      title: <Tooltip title="Hours worked divided by hours scheduled, including training time on both sides.">Adherence % <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>,
      key: 'AdhRaw', width: 130, align: 'right',
      sorter: (a, b) => {
        const ra = a.ScheduledHrs > 0 ? a.WorkedHrs / a.ScheduledHrs : -1;
        const rb = b.ScheduledHrs > 0 ? b.WorkedHrs / b.ScheduledHrs : -1;
        return ra - rb;
      },
      render: (_, r) => {
        const pct = r.WorkedHrs != null && r.ScheduledHrs > 0
          ? Math.round(r.WorkedHrs / r.ScheduledHrs * 10000) / 100 : null;
        return <AdherenceTag pct={pct} />;
      },
    },
    {
      title: <Tooltip title="Hours worked divided by hours scheduled with training shifts excluded from both sides. Used as the official adherence metric.">Adherence % Excl Training <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} /></Tooltip>,
      dataIndex: 'AdherencePct', key: 'AdherencePct', width: 175, align: 'right',
      sorter: (a, b) => (a.AdherencePct ?? -1) - (b.AdherencePct ?? -1),
      render: v => <AdherenceTag pct={v} />,
    },
    ...(canViewFinancials >= 1 ? [{
      title: 'Lost Revenue', dataIndex: 'LostRevenue', key: 'LostRevenue', width: 140, align: 'right',
      sorter: (a, b) => (a.LostRevenue || 0) - (b.LostRevenue || 0),
      render: v => (v != null && v > 0)
        ? <span style={{ color: '#ff4d4f' }}>
            ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        : '—',
    }] : []),
  ];

  const dowColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 160, fixed: 'left',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    ...DOW_ORDER.map(day => ({
      title: day.slice(0, 3), key: day, width: 90, align: 'center',
      render: (_, record) => {
        const d = record[day];
        if (!d || (d.WorkedHrs === 0 && d.ScheduledHrs === 0)) {
          return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
        }
        const notWorked = Math.max(0, d.ScheduledHrs - d.WorkedHrs);
        return (
          <Tooltip title={<span>Worked: {d.WorkedHrs.toFixed(1)}h<br />Scheduled: {d.ScheduledHrs.toFixed(1)}h</span>}>
            <div style={{ cursor: 'default', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <AdherenceTag pct={d.AdherencePct} />
              <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: 1.2 }}>{notWorked.toFixed(1)}h not worked</Typography.Text>
            </div>
          </Tooltip>
        );
      },
    })),
  ];

  const tenureColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 160, fixed: 'left',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    ...TENURE_BUCKETS.map(bucket => ({
      title: bucket, key: bucket, width: 115, align: 'center',
      render: (_, record) => {
        const d = record[bucket];
        if (!d || (d.WorkedHrs === 0 && d.ScheduledHrs === 0)) {
          return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
        }
        const notWorked = Math.max(0, d.ScheduledHrs - d.WorkedHrs);
        return (
          <Tooltip title={<span>Worked: {d.WorkedHrs.toFixed(1)}h<br />Scheduled: {d.ScheduledHrs.toFixed(1)}h</span>}>
            <div style={{ cursor: 'default', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <AdherenceTag pct={d.AdherencePct} />
              <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: 1.2 }}>{notWorked.toFixed(1)}h not worked</Typography.Text>
            </div>
          </Tooltip>
        );
      },
    })),
  ];

  const shiftColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 160,
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Shift', dataIndex: 'ShiftType', key: 'ShiftType', width: 120,
      sorter: (a, b) => SHIFT_ORDER.indexOf(a.ShiftType) - SHIFT_ORDER.indexOf(b.ShiftType),
      render: v => <span>{SHIFT_ICONS[v] || ''} {v}</span>,
    },
    {
      title: 'Total', dataIndex: 'TotalShifts', key: 'TotalShifts', width: 70, align: 'right',
      sorter: (a, b) => (a.TotalShifts || 0) - (b.TotalShifts || 0),
    },
    {
      title: 'Full',
      key: 'Full', width: 90, align: 'center',
      sorter: (a, b) => (a.FullPct ?? -1) - (b.FullPct ?? -1),
      render: (_, r) => r.TotalShifts > 0
        ? <Tooltip title={`${r.FullCount} shifts`}><span style={{ color: '#52c41a', fontWeight: 600 }}>{r.FullPct?.toFixed(1)}%</span></Tooltip>
        : '—',
    },
    {
      title: 'Late Start',
      key: 'LateStart', width: 105, align: 'center',
      sorter: (a, b) => (a.LateStartCount || 0) - (b.LateStartCount || 0),
      render: (_, r) => {
        if (!r.LateStartCount && !r.BothCount) return <Typography.Text type="secondary">—</Typography.Text>;
        const n = r.LateStartCount + r.BothCount;
        const pct = r.TotalShifts > 0 ? (n / r.TotalShifts * 100).toFixed(1) : null;
        const tip = [
          r.LateStartCount ? `${r.LateStartCount} late-start only` : null,
          r.BothCount      ? `${r.BothCount} late + early end` : null,
          r.AvgLateStartMins ? `Avg ${r.AvgLateStartMins.toFixed(0)} min late` : null,
        ].filter(Boolean).join('\n');
        return (
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
            <span style={{ color: '#faad14', fontWeight: 600, cursor: 'default' }}>
              {n} {pct ? `(${pct}%)` : ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Early End',
      key: 'EarlyEnd', width: 105, align: 'center',
      sorter: (a, b) => (a.EarlyEndCount || 0) - (b.EarlyEndCount || 0),
      render: (_, r) => {
        if (!r.EarlyEndCount && !r.BothCount) return <Typography.Text type="secondary">—</Typography.Text>;
        const n = r.EarlyEndCount + r.BothCount;
        const pct = r.TotalShifts > 0 ? (n / r.TotalShifts * 100).toFixed(1) : null;
        const tip = [
          r.EarlyEndCount ? `${r.EarlyEndCount} early end only` : null,
          r.BothCount     ? `${r.BothCount} late + early end` : null,
          r.AvgEarlyEndMins ? `Avg ${r.AvgEarlyEndMins.toFixed(0)} min early` : null,
        ].filter(Boolean).join('\n');
        return (
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
            <span style={{ color: '#faad14', fontWeight: 600, cursor: 'default' }}>
              {n} {pct ? `(${pct}%)` : ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Absent', dataIndex: 'AbsentCount', key: 'AbsentCount', width: 80, align: 'center',
      sorter: (a, b) => (a.AbsentCount || 0) - (b.AbsentCount || 0),
      render: (v, r) => v > 0
        ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{v} ({r.TotalShifts > 0 ? (v / r.TotalShifts * 100).toFixed(1) : 0}%)</span>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Avg Late (min)',
      dataIndex: 'AvgLateStartMins', key: 'AvgLateStartMins', width: 120, align: 'right',
      sorter: (a, b) => (a.AvgLateStartMins ?? -1) - (b.AvgLateStartMins ?? -1),
      render: v => v != null ? <span style={{ color: '#faad14' }}>{v.toFixed(0)} min</span> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Avg Early End (min)',
      dataIndex: 'AvgEarlyEndMins', key: 'AvgEarlyEndMins', width: 145, align: 'right',
      sorter: (a, b) => (a.AvgEarlyEndMins ?? -1) - (b.AvgEarlyEndMins ?? -1),
      render: v => v != null ? <span style={{ color: '#faad14' }}>{v.toFixed(0)} min</span> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Avg Shortfall',
      dataIndex: 'AvgShortfallHrs', key: 'AvgShortfallHrs', width: 120, align: 'right',
      sorter: (a, b) => (a.AvgShortfallHrs ?? 0) - (b.AvgShortfallHrs ?? 0),
      render: v => v != null
        ? <span style={{ color: v < 0 ? '#ff4d4f' : '#52c41a' }}>{v > 0 ? '+' : ''}{v.toFixed(1)}h</span>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
  ];

  // ── summary card helper ───────────────────────────────────────────────────
  const metricCard = (icon, label, value, suffix = '', color = undefined) => (
    <Card size="small" style={{ borderRadius: 8 }}>
      <Statistic
        title={<Space size={6}>{React.cloneElement(icon, { style: { color: color || '#1677ff' } })}<span>{label}</span></Space>}
        value={value ?? '—'}
        suffix={value != null ? suffix : ''}
        precision={value != null ? 1 : undefined}
        valueStyle={{ color: color || '#262626', fontSize: 22 }}
      />
    </Card>
  );

  // Against all paid hours -- worked plus every non-worked bucket -- rather
  // than against worked alone. That makes the shares comparable and keeps
  // their sum inside 100%: each is a slice of the same whole. No paid hours at
  // all means no denominator, which is not the same as no time off.
  const breakdownCard = (icon, label, items, total) => (
    <Card size="small" style={{ borderRadius: 8 }}>
      <Statistic
        title={<Space size={6}>{React.cloneElement(icon, { style: { color: '#1677ff' } })}<span>{label}</span></Space>}
        valueRender={() => (
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            {items.map((x) => {
              const pct = (total > 0 && x.value != null) ? (100 * x.value) / total : null;
              return (
                <div key={x.label} style={{ minWidth: 52 }}>
                  <div style={{ fontSize: 20, color: '#262626', lineHeight: 1.2 }}>
                    {x.value != null ? Number(x.value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>{x.label}</div>
                  <div style={{ fontSize: 11, color: '#bfbfbf' }}>
                    {pct != null ? `${pct.toFixed(1)}%` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      />
    </Card>
  );

  // One pagination shape for every table on the page. hideOnSinglePage keeps
  // the pivots -- which are often a handful of rows -- from growing a pager
  // that can never do anything.
  const PAGINATION = {
    defaultPageSize: 50,
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    hideOnSinglePage: true,
    showTotal: (t) => `${t} rows`,
  };

  const adherencePct    = (summary?.WorkedHrs != null && summary?.ScheduledHrs > 0)
    ? Math.round(summary.WorkedHrs / summary.ScheduledHrs * 10000) / 100
    : null;
  const adherenceClr    = adherencePct != null ? adherenceColor(adherencePct) : undefined;

  const byTeam   = viewMode === 'By Team';
  const byAgent  = viewMode === 'By Agent';
  const byDow    = viewMode === 'By Day of Week';
  const byShift  = viewMode === 'By Shift';
  const byTenure = viewMode === 'By Tenure';

  const isEmpty = byTeam   ? rows.length === 0
                : byAgent  ? agentRows.length === 0
                : byDow    ? dowPivot.length === 0
                : byTenure ? tenurePivot.length === 0
                :            shiftRows.length === 0;

  const isCurrentlyLoading = byDow ? dowLoading : byShift ? shiftLoading : loading;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {!embedded && (
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Schedule Adherence</Typography.Title>
      </div>
      )}

      {/* ── filters ── */}
      <Space wrap style={{ marginBottom: 20 }}>
        {!embedded && (
        <Select
          mode="multiple"
          maxTagCount="responsive"
          placeholder="All teams"
          value={teamIds}
          onChange={setTeamIds}
          options={teamOptions.map(t => ({ label: t.Name, value: t.ID }))}
          style={{ minWidth: 260 }}
          allowClear
          suffixIcon={<TeamOutlined />}
        />
        )}
        {allJobs.length > 0 && (
          <Select
            mode="multiple"
            placeholder="All jobs"
            value={jobIds}
            onChange={setJobIds}
            options={allJobs.map(j => ({ label: j.JobName, value: j.JobID }))}
            style={{ minWidth: 160 }}
            allowClear
            maxTagCount="responsive"
          />
        )}
        {/* Guarded one by one, not wrapped in a fragment: Space spaces its
            direct children, so a fragment would collapse these two into one
            and lose the gap between them. A false child is dropped. */}
        {!embedded && (
        <DatePicker
          picker="month" value={fromMonth} onChange={v => v && setFromMonth(v)}
          allowClear={false} placeholder="From month" format="MMM YYYY"
          disabledDate={d => toMonth && d.isAfter(toMonth, 'month')}
        />
        )}
        {!embedded && (
        <DatePicker
          picker="month" value={toMonth} onChange={v => v && setToMonth(v)}
          allowClear={false} placeholder="To month" format="MMM YYYY"
          disabledDate={d => fromMonth && d.isBefore(fromMonth, 'month')}
        />
        )}
        {viewMode !== 'By Shift' && (
          <Space size={6}>
            <Switch size="small" checked={activeOnly} onChange={setActiveOnly} />
            <Typography.Text style={{ fontSize: 13 }}>Only Active Employees</Typography.Text>
          </Space>
        )}
      </Space>

      {/* ── summary metrics ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        <>
          {(() => {
            const showLR     = (byTeam || byAgent) && canViewFinancials >= 1;
            const notWorked  = Math.max(0, (summary?.ScheduledHrs ?? 0) - (summary?.WorkedHrs ?? 0));
            return (
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'nowrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>{metricCard(<ClockCircleOutlined />, 'Hours Worked',      summary?.WorkedHrs,   ' hrs')}</div>
                <div style={{ flex: 1, minWidth: 0 }}>{metricCard(<BarChartOutlined />,   'Hours Scheduled',   summary?.ScheduledHrs, ' hrs')}</div>
                <div style={{ flex: 1, minWidth: 0 }}>{metricCard(<ClockCircleOutlined />, 'Sche. Hours Not Worked',  summary != null ? notWorked : null, ' hrs')}</div>
                <div style={{ flex: 1, minWidth: 0 }}>{metricCard(<CheckCircleOutlined />, 'Schedule Adherence', adherencePct, '%', adherenceClr)}</div>
                {showLR && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Card size="small" style={{ borderRadius: 8 }}>
                      <Statistic
                        title={<Space size={6}><DollarOutlined style={{ color: '#1677ff' }} /><span>Lost Revenue</span></Space>}
                        value={Math.max(0, summary?.LostRevenue ?? 0)}
                        formatter={v => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        valueStyle={{ color: (summary?.LostRevenue ?? 0) > 0 ? '#ff4d4f' : '#262626', fontSize: 22 }}
                      />
                    </Card>
                  </div>
                )}
                <div style={{ flex: 1.6, minWidth: 0 }}>{breakdownCard(<ClockCircleOutlined />, 'Time Off (hrs, % of total paid)', [
                    { label: 'PTO',     value: summary?.PTOOnlyHrs },
                    { label: 'Sick',    value: summary?.SickHrs },
                    { label: 'Holiday', value: summary?.HolidayHrs },
                    { label: 'Other',   value: summary?.OtherOnlyHrs },
                  ],
                  (summary?.WorkedHrs ?? 0) + (summary?.PTOHrs ?? 0) + (summary?.OtherHrs ?? 0))}</div>
              </div>
            );
          })()}

          {/* ── view toggle + legend ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
            <Segmented
              options={[
                { label: <Space size={4}><TeamOutlined />By Team</Space>,             value: 'By Team'        },
                { label: <Space size={4}><UserOutlined />By Agent</Space>,            value: 'By Agent'       },
                { label: <Space size={4}><CalendarOutlined />By Day of Week</Space>,  value: 'By Day of Week' },
                { label: <Space size={4}><ScheduleOutlined />By Shift</Space>,        value: 'By Shift'       },
                { label: <Space size={4}><ClockCircleOutlined />By Tenure</Space>,    value: 'By Tenure'      },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
            {!byShift && (
              <Space size={16}>
                {[
                  { label: '> 97%',  color: '#52c41a' },
                  { label: '90-97%', color: '#faad14' },
                  { label: '< 90%',  color: '#ff4d4f' },
                ].map(({ label, color }) => (
                  <Space key={label} size={4}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}</Typography.Text>
                  </Space>
                ))}
              </Space>
            )}
            {byShift && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Late Start / Early End counts include shifts with both issues. Hover cells for detail. 10-min tolerance window.
              </Typography.Text>
            )}
          </div>

          {/* ── detail table ── */}
          {isCurrentlyLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : isEmpty ? (
            <Empty
              description={byShift ? 'No shift data — run Refresh-AgentShiftAdherence.ps1 first' : 'No adherence data for the selected filters'}
              style={{ padding: 40 }}
            />
          ) : byTeam ? (
            <Table rowKey={r => `${r.TeamID}-${r.MonthKey}`} dataSource={rows} columns={teamColumns}
              size="small" pagination={PAGINATION} scroll={{ x: true }} sticky />
          ) : byAgent ? (
            <Table rowKey={r => r.AgentID} dataSource={agentRows} columns={agentColumns}
              size="small" pagination={PAGINATION} scroll={{ x: true }} sticky />
          ) : byDow ? (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Hover a cell to see worked vs. scheduled hours. Blank = no data for that day.
              </Typography.Text>
              <Table rowKey={r => r.TeamID} dataSource={dowPivot} columns={dowColumns}
                size="small" pagination={PAGINATION} scroll={{ x: true }} bordered sticky />
            </>
          ) : byTenure ? (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Tenure based on rehire date (if applicable) or original hire date. Hover a cell to see worked vs. scheduled hours.
              </Typography.Text>
              <Table rowKey={r => r.TeamID} dataSource={tenurePivot} columns={tenureColumns}
                size="small" pagination={PAGINATION} scroll={{ x: true }} bordered sticky />
            </>
          ) : (
            <Table
              rowKey={r => `${r.TeamName}-${r.ShiftType}`}
              dataSource={shiftRows}
              columns={shiftColumns}
              size="small"
              pagination={PAGINATION}
              scroll={{ x: true }}
              bordered
              sticky
            />
          )}
        </>
      )}
    </>
  );
}
